import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, EyeOff, X } from 'lucide-react';
import { apiFetch, ApiError } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Button } from './ui/button';

type Action = 'approve' | 'reject' | 'unhide';

export interface ActionablePost {
  _id: string;
  review_status: string;
  is_hidden?: boolean;
}

interface ModerationActionsProps {
  post: ActionablePost;
  /** When 'compact', renders small icon buttons for table rows. */
  variant?: 'full' | 'compact';
  /** Called after a successful action with the server's result. */
  onDone?: () => void;
}

interface ActionResult {
  ok: true;
  noOp: boolean;
  before: { review_status: string; is_hidden: boolean };
  after: { review_status: string; is_hidden: boolean };
}

async function callAction(
  action: Action,
  postId: string,
  reason: string,
): Promise<ActionResult> {
  return apiFetch<ActionResult>(`/api/moderation/posts/${postId}/${action}`, {
    method: 'POST',
    body: JSON.stringify({ reason: reason || undefined }),
  });
}

export function ModerationActions({
  post,
  variant = 'compact',
  onDone,
}: ModerationActionsProps): JSX.Element {
  const qc = useQueryClient();
  const [confirming, setConfirming] = useState<Action | null>(null);
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: ({
      action,
      reasonText,
    }: {
      action: Action;
      reasonText: string;
    }) => callAction(action, post._id, reasonText),
    onSuccess: () => {
      // Anything that depends on review_status / is_hidden / counts. Bulldoze
      // these scopes — the server cache is also invalidated.
      void qc.invalidateQueries({ queryKey: ['metrics', 'moderation'] });
      void qc.invalidateQueries({ queryKey: ['metrics', 'content'] });
      void qc.invalidateQueries({ queryKey: ['metrics', 'overview'] });
      setConfirming(null);
      setReason('');
      setError(null);
      onDone?.();
    },
    onError: (err) => {
      if (err instanceof ApiError) setError(err.message);
      else setError('Action failed.');
    },
  });

  function run(action: Action): void {
    setError(null);
    // Reject always asks for confirmation (with optional reason). Approve +
    // unhide go immediately — they're reversible.
    if (action === 'reject') {
      setConfirming('reject');
      return;
    }
    mutation.mutate({ action, reasonText: '' });
  }

  function confirmReject(): void {
    mutation.mutate({ action: 'reject', reasonText: reason });
  }

  // Hide buttons that wouldn't change anything (e.g. approve on an
  // already-approved + visible post). The server still no-ops + logs if
  // somehow triggered, but reducing button noise keeps the table readable.
  const isApproved = post.review_status === 'approved' && !post.is_hidden;
  const isRejected = post.review_status === 'rejected' && post.is_hidden;
  const isHidden = post.is_hidden === true;

  const isCompact = variant === 'compact';
  const btnSize = isCompact ? 'sm' : 'md';
  const busy = mutation.isPending;

  if (confirming === 'reject') {
    return (
      <div
        className={cn(
          'flex flex-col gap-2',
          isCompact ? '' : 'rounded-md border border-state-danger/30 p-3',
        )}
      >
        <textarea
          autoFocus
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason (optional, logged in audit)"
          rows={isCompact ? 2 : 3}
          className={cn(
            'w-full rounded-md border border-surface-divider bg-transparent p-2 text-sm text-fg placeholder:text-fg-subtle',
            'focus:outline-none focus:border-state-danger',
          )}
          disabled={busy}
        />
        <div className="flex gap-2">
          <Button
            variant="primary"
            size={btnSize}
            onClick={confirmReject}
            disabled={busy}
          >
            {busy ? 'Rejecting…' : 'Confirm reject'}
          </Button>
          <Button
            variant="secondary"
            size={btnSize}
            onClick={() => {
              setConfirming(null);
              setReason('');
              setError(null);
            }}
            disabled={busy}
          >
            Cancel
          </Button>
        </div>
        {error && (
          <div className="text-xs text-state-danger">{error}</div>
        )}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      {!isApproved && (
        <Button
          variant="secondary"
          size={btnSize}
          onClick={() => run('approve')}
          disabled={busy}
          title="Approve (sets review_status=approved, is_hidden=false)"
        >
          <Check size={14} className="text-brand-cyan" />
          {!isCompact && <span>Approve</span>}
        </Button>
      )}
      {!isRejected && (
        <Button
          variant="secondary"
          size={btnSize}
          onClick={() => run('reject')}
          disabled={busy}
          title="Reject (sets review_status=rejected, is_hidden=true)"
        >
          <X size={14} className="text-state-danger" />
          {!isCompact && <span>Reject</span>}
        </Button>
      )}
      {isHidden && (
        <Button
          variant="ghost"
          size={btnSize}
          onClick={() => run('unhide')}
          disabled={busy}
          title="Unhide (sets is_hidden=false; review_status untouched)"
        >
          <EyeOff size={14} />
          {!isCompact && <span>Unhide</span>}
        </Button>
      )}
      {error && (
        <span className="text-xs text-state-danger ml-1">{error}</span>
      )}
    </div>
  );
}
